import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { TextBox, TextBoxComponentSpacing, TextBoxLayoutStyle } from './TextBox';
import { FocusInEvent } from 'devextreme/ui/text_box';

interface CreditCardBoxProps {
    id?: string;
    dataTestid?: string;
    value: string | undefined;
    name?: string;
    readOnly?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: string;
    required?: boolean;
    children?: React.ReactNode;
    width?: string;
    maxLength?: number;
    onChange: (value: string) => void;
    onEnterKey?: () => void;
    onFocusIn?: ((e: FocusInEvent) => void) | undefined;
    onValidationEvent?: (isValid: boolean) => void;
    componentSpacing?: TextBoxComponentSpacing;
    showInfoIcon?: boolean;
    infoText?: ReactNode;
    /** Label above the input (Vertical, default) or beside it (Horizontal). */
    layoutStyle?: TextBoxLayoutStyle;
    /** Label column width when layoutStyle is Horizontal. Defaults to 120px. */
    labelWidth?: string;
    /** Total height of the input box, border included (e.g. '36px'). Omit to keep the default height. */
    height?: string;
}

export const CreditCardBox: React.FC<CreditCardBoxProps> = ({
    id,
    dataTestid,
    value,
    readOnly = false,
    disabled = false,
    label,
    helperText,
    required = false,
    children,
    width = 'auto',
    maxLength,
    onChange,
    onEnterKey,
    onFocusIn,
    onValidationEvent,
    componentSpacing,
    showInfoIcon = false,
    infoText,
    layoutStyle,
    labelWidth,
    height,
}) => {
    const [currentValue, set_currentValue] = useState(value);

    const validateValue = (value: string) => {
        if (!value) return false;

        const anyUnderscore = value.includes('_');
        return !anyUnderscore;
    };

    useEffect(() => {
        if (!value) return;

        set_currentValue(value.replace(/[^0-9]/g, ''));
    }, [value]);

    return (
        <TextBox
            id={id}
            dataTestid={dataTestid}
            value={currentValue}
            onEnterKey={onEnterKey}
            readOnly={readOnly}
            onChange={onChange}
            validateValue={validateValue}
            onValidationEvent={onValidationEvent}
            onFocusIn={onFocusIn}
            label={label}
            helperText={helperText}
            required={required}
            children={children}
            width={width}
            maxLength={maxLength}
            type='text'
            mask='0000 0000 0000 0000'
            maskChar='•'
            maskInvalidMessage='Invalid card number'
            componentSpacing={componentSpacing}
            disabled={disabled}
            showInfoIcon={showInfoIcon}
            infoText={infoText}
            layoutStyle={layoutStyle}
            labelWidth={labelWidth}
            height={height}
        />
    );
};
